import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { EntityIcon, ObjEntity, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import type { Action } from "../lib/actions.ts";
import { useConversation } from "../lib/conversation.ts";
import { useEntities } from "../lib/data.ts";
import { asSaved, type Row, renamesOf, rowsOf, toField } from "../lib/fieldRows.ts";
import { spaceApi, spaceUrl } from "../lib/paths.ts";
import { useAction, useResource } from "../lib/resource.ts";
import { canEditSchema } from "../lib/roles.ts";
import { location, navigate } from "../lib/router.ts";
import type { PageProps } from "../lib/state.ts";
import { toast } from "../lib/toast.ts";
import { Button } from "../ui/Button.tsx";
import { ConfirmDialog } from "../ui/Dialog.tsx";
import { Checkbox, Field, Form, Input, Textarea } from "../ui/form.tsx";
import { TrashIcon } from "../ui/icons.tsx";
import { Async, Notice } from "../ui/misc.tsx";
import { ConversationLabel, EntityConversation } from "./comments/conversations.tsx";
import { FieldList } from "./entity/fieldList.tsx";
import { IconPicker } from "./entity/iconPicker.tsx";
import { ItemsNav } from "./items/nav.tsx";

/** Create/update; renamed keys are sent as `renames` (remapping existing items) unless unchecked. */
function useSave(space: SpaceOut, entity: ObjEntity | null, onSaved: () => void) {
  const [ver, setVer] = useState(entity?.ver);
  return useAction(
    async (body: {
      name: string;
      description: string;
      icon: EntityIcon | null;
      pinned: boolean;
      rows: Row[];
      remap: boolean;
    }) => {
      const iconChanged = JSON.stringify(body.icon) !== JSON.stringify(entity?.icon ?? null);
      const doc = {
        name: body.name,
        description: body.description || null,
        ...(iconChanged && { icon: body.icon }),
        pinned: body.pinned,
        fields: body.rows.map((r) => toField(r)),
      };
      const renames = renamesOf(body.rows);
      if (entity) {
        const remap = body.remap && Object.keys(renames).length > 0;
        const saved = await api.patch<ObjEntity>(spaceApi(space, `/entities/${entity.id}`), {
          ...doc,
          ver,
          ...(remap && { renames }),
        });
        setVer(saved.ver);
        onSaved();
      } else {
        const created = await api.post<ObjEntity>(spaceApi(space, "/entities"), doc);
        navigate(spaceUrl(space, `/entities/${created.id}`), true);
      }
      toast(t("entities.saved"));
    },
  );
}

function EntityForm({
  space,
  entity,
  entities,
}: {
  space: SpaceOut;
  entity: ObjEntity | null;
  entities: readonly ObjEntity[];
}) {
  const [name, setName] = useState(entity?.name ?? "");
  const [description, setDescription] = useState(entity?.description ?? "");
  const [icon, setIcon] = useState<EntityIcon | null>(entity?.icon ?? null);
  const [pinned, setPinned] = useState(entity?.pinned ?? false);
  const [rows, setRows] = useState<Row[]>(() => rowsOf(entity?.fields ?? []));
  const [remap, setRemap] = useState(true);
  const [save, saving] = useSave(space, entity, () => setRows(asSaved));
  const renames = Object.entries(renamesOf(rows));
  const readOnly = !canEditSchema(space);
  return (
    <Form onSubmit={() => save({ name, description, icon, pinned, rows, remap })}>
      <fieldset disabled={readOnly} class="flex min-w-0 flex-col gap-4">
        <div class="flex max-w-3xl flex-col gap-4">
          <div class="flex items-end gap-3">
            <IconPicker value={icon} onChange={setIcon} disabled={readOnly} />
            <div class="flex-1">
              <Field label={t("entities.name")}>
                <Input value={name} onValue={setName} required={true} />
              </Field>
            </div>
          </div>
          <Field label={t("entities.description")}>
            <Textarea value={description} onValue={setDescription} />
          </Field>
          <div class="flex flex-col gap-1">
            <Checkbox label={t("entities.pinned")} checked={pinned} onCheck={setPinned} />
            <p class="pl-6 text-xs text-zinc-500">{t("entities.pinnedHint")}</p>
          </div>
        </div>
        <FieldList
          rows={rows}
          setRows={setRows}
          env={{ entities, orgZone: space.time_zone, readOnly, space, saved: entity }}
        />
        {renames.length > 0 ? (
          <Checkbox
            label={t("entities.remap", { renames: renames.map(([a, b]) => `${a} → ${b}`).join(", ") })}
            checked={remap}
            onCheck={setRemap}
          />
        ) : null}
        <div class="flex justify-end">
          <Button variant="primary" submit={true} busy={saving} answer="confirm">
            {t("common.save")}
          </Button>
        </div>
      </fieldset>
    </Form>
  );
}

function useDeleteEntity(space: SpaceOut, entity: ObjEntity): { action: Action; dialog: ComponentChildren } {
  const [asking, setAsking] = useState(false);
  const [remove, removing] = useAction(async () => {
    await api.del(spaceApi(space, `/entities/${entity.id}`));
    navigate(spaceUrl(space, "/entities"));
  });
  return {
    action: {
      key: "delete",
      label: t("common.delete"),
      icon: <TrashIcon />,
      symbol: "trash",
      danger: true,
      overflow: true,
      busy: removing,
      onSelect: () => setAsking(true),
    },
    dialog: (
      <ConfirmDialog
        open={asking}
        onClose={() => setAsking(false)}
        title={t("entities.deleteTitle")}
        message={t("entities.deleteMessage")}
        confirmLabel={t("common.delete")}
        onConfirm={() => remove()}
      />
    ),
  };
}

/** An entity's page: its fields (the form), or (`?tab=conversation`) its conversation. */
function EntityPage({
  space,
  entity,
  editable,
  entities,
}: {
  space: SpaceOut;
  entity: ObjEntity;
  editable: boolean;
  entities: readonly ObjEntity[];
}) {
  const conv = useConversation(space, { entity: entity.id });
  const deleting = useDeleteEntity(space, entity);
  const talking = location.value.searchParams.get("tab") === "conversation";
  return (
    <>
      <ItemsNav
        space={space}
        tab={talking ? "conversation" : "fields"}
        entity={entity}
        conversation={<ConversationLabel conv={conv} />}
        buttons={editable ? [deleting.action] : []}
      />
      {deleting.dialog}
      {talking ? (
        <EntityConversation space={space} entity={entity} conv={conv} />
      ) : (
        <>
          <Notice when={space.schema_locked}>{t("entities.locked")}</Notice>
          <EntityForm key={entity.id} space={space} entity={entity} entities={entities} />
        </>
      )}
    </>
  );
}

export function EntityEditor({ space, params }: PageProps) {
  const id = params["id"] ?? "new";
  const r = useResource(`entity:${space.id}:${id}`, (signal) =>
    id === "new" ? Promise.resolve(null) : api.get<ObjEntity>(spaceApi(space, `/entities/${id}`), undefined, signal),
  );
  const entities = useEntities(space);
  const editable = canEditSchema(space);
  return (
    <Async r={r}>
      {(e) => (
        <>
          {e ? (
            <EntityPage space={space} entity={e} editable={editable} entities={entities.data ?? []} />
          ) : (
            <>
              <ItemsNav space={space} tab="entities" title={t("entities.new")} />
              <Notice when={space.schema_locked}>{t("entities.locked")}</Notice>
              <EntityForm key="new" space={space} entity={null} entities={entities.data ?? []} />
            </>
          )}
        </>
      )}
    </Async>
  );
}
